/**
 * Vuori-style fabric features row — 3-4 small icon+label pairs that
 * highlight the technical properties of the product's fabric.
 *
 * Renders below the size selector / above the accordions on the PDP
 * (matches Vuori's placement). Bilingual via `locale` prop.
 *
 * The feature list is selected from a per-fabric mapping. Falls back
 * to a generic "soft / stretchy / breathable" set when the product
 * has no `fabric` value set.
 *
 * Icons are inline SVG (no external dep, no extra request). Style
 * matches the rest of Diolo's PDP — thin 1.5px stroke, currentColor,
 * 18px box.
 */
import type { Locale } from "~/utils/locale";

interface FeatureDef {
  key: string;
  labelEn: string;
  labelVi: string;
  icon: () => JSX.Element;
}

const ICONS = {
  soft: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
    </svg>
  ),
  moisture: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 2.25c-2.5 4-6 7.5-6 11a6 6 0 0012 0c0-3.5-3.5-7-6-11z" />
    </svg>
  ),
  stretch: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      {/* Plus-cross with small arrowheads at each tip. Tighter +
          smaller wings than v1 so it reads as 4-way arrows instead
          of a diamond shape. v1 had a typo on the bottom-right wing
          that drew off-canvas (M12 21l4 4 instead of M12 21l4-4) —
          that's what the user saw as "weird". Replaced with
          consistently tight 2-unit wings. */}
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4v16 M4 12h16 M12 4l-2 2 M12 4l2 2 M12 20l-2-2 M12 20l2-2 M4 12l2-2 M4 12l2 2 M20 12l-2-2 M20 12l-2 2"
      />
    </svg>
  ),
  light: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 12c0-3.5-3-9-9-9-2 4 0 7 2 9-2 1-4 3-4 6a4 4 0 008 0c0-2-1-3.5-2-5 2 0 5-1 5-1z" />
    </svg>
  ),
  breathable: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8h12.5a2.5 2.5 0 100-5M3 14h17a2.5 2.5 0 110 5M3 20h7" />
    </svg>
  ),
  quickDry: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  premium: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 9l4-6h10l4 6-9 12L3 9z M9 9h6 M3 9h18" />
    </svg>
  ),
  noSeams: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18" />
      <circle cx="6" cy="12" r="0.5" fill="currentColor" />
      <circle cx="12" cy="12" r="0.5" fill="currentColor" />
      <circle cx="18" cy="12" r="0.5" fill="currentColor" />
    </svg>
  ),
  hugsBody: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 2a4 4 0 014 4M12 2a4 4 0 00-4 4M6 8c0 4 2 6 6 14 4-8 6-10 6-14a6 6 0 00-12 0z" />
    </svg>
  ),
  smooth: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12c2-3 4-3 6 0s4 3 6 0 4-3 6 0" />
    </svg>
  ),
};

const FEATURES: Record<string, FeatureDef> = {
  soft: { key: "soft", labelEn: "Buttery Soft", labelVi: "Mềm mịn", icon: ICONS.soft },
  moisture: { key: "moisture", labelEn: "Moisture Wicking", labelVi: "Thấm hút mồ hôi", icon: ICONS.moisture },
  stretch: { key: "stretch", labelEn: "4-Way Stretch", labelVi: "Co giãn 4 chiều", icon: ICONS.stretch },
  light: { key: "light", labelEn: "Lightweight", labelVi: "Nhẹ thoáng", icon: ICONS.light },
  breathable: { key: "breathable", labelEn: "Breathable", labelVi: "Thoáng khí", icon: ICONS.breathable },
  quickDry: { key: "quickDry", labelEn: "Quick Dry", labelVi: "Khô nhanh", icon: ICONS.quickDry },
  premium: { key: "premium", labelEn: "Premium", labelVi: "Cao cấp", icon: ICONS.premium },
  noSeams: { key: "noSeams", labelEn: "No Seams", labelVi: "Không đường may", icon: ICONS.noSeams },
  hugsBody: { key: "hugsBody", labelEn: "Hugs Body", labelVi: "Ôm sát body", icon: ICONS.hugsBody },
  smooth: { key: "smooth", labelEn: "Smooth Drape", labelVi: "Mềm rủ", icon: ICONS.smooth },
};

// Feature set per fabric type. Keep to 4 max to avoid crowding the PDP.
const FABRIC_TO_FEATURES: Record<string, string[]> = {
  cloud: ["soft", "moisture", "stretch", "light"],
  rib: ["soft", "stretch", "hugsBody", "breathable"],
  lux: ["premium", "smooth", "soft", "stretch"],
  smooth: ["soft", "light", "quickDry", "moisture"],
  seamless: ["noSeams", "hugsBody", "stretch", "breathable"],
};

const DEFAULT_FEATURES = ["soft", "stretch", "breathable", "quickDry"];

// Merchant feedback 2026-05-13:
//   1. Accessories (visor, socks, caps, tumblers) were showing the
//      apparel-default "Buttery Soft / 4-Way Stretch / Breathable /
//      Quick Dry" row. None of those properties apply to a visor or
//      tumbler — sloppy. Now we suppress the row entirely when
//      `productType === "Accessories"`.
//   2. Round 1 of the fix was over-eager: it returned null whenever
//      `fabric` was missing, but EVERY apparel product in prod DB
//      currently has `fabric: null` (the merchant hasn't backfilled
//      the field yet). So *all* PDPs lost the icon row, not just
//      accessories. Now we fall back to DEFAULT_FEATURES for any
//      non-accessory product so apparel keeps the icons until the
//      fabric backfill happens.
interface Props {
  fabric: string | null | undefined;
  productType?: string | null;
  locale: Locale;
}

export function FabricFeatures({ fabric, productType, locale }: Props) {
  // Accessories never get a fabric-features row.
  if (productType && /accessor/i.test(productType)) return null;
  const featureKeys =
    fabric && FABRIC_TO_FEATURES[fabric.toLowerCase()]
      ? FABRIC_TO_FEATURES[fabric.toLowerCase()]
      : DEFAULT_FEATURES;
  const features = featureKeys.map((k) => FEATURES[k]).filter(Boolean);

  return (
    <ul
      className="grid grid-cols-4 gap-x-2 sm:gap-x-3 gap-y-4 mt-6 text-[#555]"
      aria-label={locale === "vi" ? "Đặc tính vải" : "Fabric features"}
    >
      {features.map((f) => (
        <li key={f.key} className="flex flex-col items-center text-center px-1 sm:px-2">
          <div className="text-black mb-1.5">{f.icon()}</div>
          <span className="text-[10px] uppercase tracking-[0.08em] text-[#555] leading-tight">
            {locale === "vi" ? f.labelVi : f.labelEn}
          </span>
        </li>
      ))}
    </ul>
  );
}
